import React from "react";

const fmt = (d) => {
  if (!d) return null;
  const s = d.split("T")[0];
  const [y, m, day] = s.split("-");
  return `${m}/${day}/${y}`;
};

const REGULAR_STAGES = [
  { key: "Uploaded_date", label: "Upload Date", stageId: 1 },
  { key: "Assign_date", label: "Arbitrator Assigned", stageId: 2 },
  { key: "Acc_date", label: "Acceptance", stageId: 3 },
  { key: "Soc_date", label: "SOC", stageId: 4 },
  { key: "Sec_17_app_date", label: "Sec 17 Application", stageId: 5 },
  { key: "First_hearing_date", label: "First Hearing", stageId: 6 },
  { key: "Second_hearing_date", "label": "Second Hearing", stageId: 7 },
  { key: "third_hearing_date", label: "Third Hearing", stageId: 8 },
  { key: "Award_date", label: "Award", stageId: 9 },
];

const CONTEST_STATUS_ORDER = ["pending", "assigned", "notice_generated", "award_uploaded", "done"];
const CONTEST_STAGES = [
  { status: "assigned", label: "Hearing Assigned" },
  { status: "notice_generated", label: "Notice Generated" },
  { status: "award_uploaded", label: "Award Uploaded" },
  { status: "done", label: "Completed" },
];

const dot = (status, accent) => ({
  width: 24, height: 24, borderRadius: "50%",
  display: "flex", alignItems: "center", justifyContent: "center",
  position: "absolute", left: -36, flexShrink: 0,
  fontSize: 11, fontWeight: 700,
  background:
    status === "done" ? (accent || "#198754") :
      status === "current" ? (accent || "#0d6efd") : "#ced4da",
  color: status === "pending" ? "#6c757d" : "#fff",
  border: status === "current" ? `3px solid ${accent ? accent + "66" : "#9ec5fe"}` : "2px solid transparent",
  boxShadow: status === "current" ? `0 0 0 4px ${accent ? accent + "22" : "rgba(13,110,253,.12)"}` : "none",
  transition: "all .2s",
});

const labelStyle = (status, accent) => ({
  fontWeight: status === "current" ? 700 : status === "done" ? 500 : 400,
  color:
    status === "done" ? (accent || "#198754") :
      status === "current" ? (accent || "#0d6efd") : "#9ca3af",
  fontSize: 13,
  lineHeight: 1.3,
});

const badge = (bg, color) => ({
  display: "inline-block", fontSize: 9, fontWeight: 700,
  padding: "2px 7px", borderRadius: 20, marginLeft: 8,
  background: bg, color, verticalAlign: "middle", letterSpacing: 0.4,
  textTransform: "uppercase",
});

const CaseJourneyTimeline = ({ caseData, showRegularOnly = false, showContestedOnly = false }) => {
  if (!caseData) return null;

  const isContested = !!caseData.Contested_date;
  const isOnHold = caseData.Is_hold === "1";
  const isTerminated = caseData.Is_terminate === "1";
  const holdStageId = Number(caseData.Hold_stage_id);
  const termStageId = Number(caseData.Stage_id);
  const contestStatus = caseData.Contest_status;

  // Find current regular stage index (first stage without a date)
  let currentRegIdx = REGULAR_STAGES.length;
  for (let i = 0; i < REGULAR_STAGES.length; i++) {
    if (!caseData[REGULAR_STAGES[i].key]) { currentRegIdx = i; break; }
  }

  const regStatus = (idx) =>
    idx < currentRegIdx ? "done" : idx === currentRegIdx ? "current" : "pending";

  const contestStageStatus = (st) => {
    const cur = CONTEST_STATUS_ORDER.indexOf(contestStatus);
    const idx = CONTEST_STATUS_ORDER.indexOf(st);
    if (cur === -1 || idx === -1) return "pending";
    if (idx < cur) return "done";
    if (idx === cur) return "current";
    return "pending";
  };

  // Derive a clean "where is the case now" summary label
  const currentStageName = (() => {
    if (isTerminated) {
      const s = REGULAR_STAGES.find(s => s.stageId === termStageId);
      return `Terminated at ${s?.label || "Stage " + termStageId}`;
    }
    if (isOnHold) {
      const s = REGULAR_STAGES.find(s => s.stageId === holdStageId);
      return `On Hold at ${s?.label || "Stage " + holdStageId}`;
    }
    if (isContested && contestStatus) {
      const idx = CONTEST_STATUS_ORDER.indexOf(contestStatus);
      if (idx >= 1) {
        const cs = CONTEST_STAGES.find(c => c.status === contestStatus);
        return cs ? `Contested — ${cs.label}` : "Contested";
      }
    }
    if (currentRegIdx < REGULAR_STAGES.length)
      return `Awaiting ${REGULAR_STAGES[currentRegIdx].label}`;
    return "Award Passed";
  })();

  return (
    <div style={{ fontFamily: "inherit" }}>

      {/* ── Status pill (only show if not split view) ───────────────────────────────────── */}
      {!showRegularOnly && !showContestedOnly && (
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          marginBottom: 16, padding: "10px 16px",
          background: isTerminated ? "#fff1f2" : isOnHold ? "#fffbeb" : isContested ? "#f0fdfa" : "#f0fdf4",
          border: `1px solid ${isTerminated ? "#fca5a5" : isOnHold ? "#fcd34d" : isContested ? "#99f6e4" : "#86efac"}`,
          borderRadius: 10,
        }}>
          <span style={{ fontSize: 22 }}>
            {isTerminated ? "🚫" : isOnHold ? "⏸" : isContested ? "⚖️" : "📋"}
          </span>
          <div>
            <div style={{
              fontWeight: 700, fontSize: 13,
              color: isTerminated ? "#991b1b" : isOnHold ? "#92400e" : isContested ? "#065f46" : "#166534"
            }}>
              Current Stage: {currentStageName}
            </div>
            {isOnHold && caseData.Hold_reason && (
              <div style={{ fontSize: 11, color: "#92400e", marginTop: 2 }}>
                Reason: {caseData.Hold_reason} &nbsp;·&nbsp; Since {fmt(caseData.Hold_date)}
              </div>
            )}
            {isTerminated && caseData.Remark && (
              <div style={{ fontSize: 11, color: "#991b1b", marginTop: 2 }}>
                Reason: {caseData.Remark} &nbsp;·&nbsp; On {fmt(caseData.Termination_date)}
              </div>
            )}
            {isContested && !isTerminated && !isOnHold && caseData.Contest_next_hearing_date && (
              <div style={{ fontSize: 11, color: "#065f46", marginTop: 2 }}>
                Next hearing: {fmt(caseData.Contest_next_hearing_date)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Regular case journey ──────────────────────────── */}
      {!showContestedOnly && (
        <>
          {/* <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", letterSpacing: 1, textTransform: "uppercase", marginBottom: 10 }}>
            Case Journey
          </div> */}
          <div style={{ position: "relative", paddingLeft: 36 }}>
            {/* vertical line */}
            <div style={{
              position: "absolute", left: 11, top: 10, bottom: 10,
              width: 2, background: "#e5e7eb", zIndex: 0,
            }} />
            {REGULAR_STAGES.map((stage, idx) => {
              const status = regStatus(idx);
              const date = caseData[stage.key];
              const isHeldHere = isOnHold && holdStageId === stage.stageId;
              const isTerminatedHere = isTerminated && termStageId === stage.stageId;
              return (
                <div key={stage.key} style={{ position: "relative", display: "flex", alignItems: "flex-start", marginBottom: 14, zIndex: 1 }}>
                  <div style={dot(status)}>
                    {status === "done" ? "✓" : status === "current" ? "◉" : ""}
                  </div>
                  <div>
                    <span style={labelStyle(status)}>{stage.label}</span>
                    {date && (
                      <span style={{ fontSize: 11, color: "#6b7280", marginLeft: 8 }}>
                        {fmt(date)}
                      </span>
                    )}
                    {status === "current" && !isOnHold && !isTerminated && (
                      <span style={badge("#0d6efd", "#fff")}>Current</span>
                    )}
                    {isHeldHere && <span style={badge("#f59e0b", "#000")}>Held Here</span>}
                    {isTerminatedHere && <span style={badge("#ef4444", "#fff")}>Terminated</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── Contested journey (only if contested) ─────────── */}
      {!showRegularOnly && isContested && (
        <>
          <div style={{
            fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase",
            color: "#0e7490", marginTop: 20, marginBottom: 10,
            display: "flex", alignItems: "center", gap: 6,
          }}>
            {/* <span>⚖️</span> Contested Case Journey */}
          </div>
          <div style={{ position: "relative", paddingLeft: 36 }}>
            <div style={{
              position: "absolute", left: 11, top: 10, bottom: 10,
              width: 2, background: "#a5f3fc", zIndex: 0,
            }} />
            {/* Contested Filed — always done once Contested_date exists */}
            <div style={{ position: "relative", display: "flex", alignItems: "flex-start", marginBottom: 14, zIndex: 1 }}>
              <div style={dot("done", "#0e7490")}>✓</div>
              <div>
                <span style={{ ...labelStyle("done", "#0e7490") }}>Contested Filed</span>
                <span style={{ fontSize: 11, color: "#6b7280", marginLeft: 8 }}>{fmt(caseData.Contested_date)}</span>
              </div>
            </div>
            {CONTEST_STAGES.map((cs) => {
              const status = contestStageStatus(cs.status);
              return (
                <div key={cs.status} style={{ position: "relative", display: "flex", alignItems: "flex-start", marginBottom: 14, zIndex: 1 }}>
                  <div style={dot(status, "#0e7490")}>
                    {status === "done" ? "✓" : status === "current" ? "◉" : ""}
                  </div>
                  <div>
                    <span style={labelStyle(status, "#0e7490")}>{cs.label}</span>
                    {status === "current" && (
                      <span style={badge("#0e7490", "#fff")}>Current</span>
                    )}
                    {cs.status === "assigned" && caseData.Contest_next_hearing_date && status !== "pending" && (
                      <span style={{ fontSize: 11, color: "#6b7280", marginLeft: 8 }}>
                        {fmt(caseData.Contest_next_hearing_date)}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default CaseJourneyTimeline;
